import React, { useEffect, useRef, useState } from 'react'
import { Play, Spinner } from '@phosphor-icons/react'
import { Icon } from '@shopflo/ui'
import { V2AppMedia } from 'src/lib/types/appsIntegrations'

interface VideoTileProps {
  media: V2AppMedia
  fallbackLabel: string
  isPlaying: boolean
  onPlay: () => void
}

const YOUTUBE_RE =
  /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|v\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
const VIMEO_RE = /(?:vimeo\.com\/)(\d+)/
const DIRECT_VIDEO_RE = /\.(mp4|webm|mov|m4v|ogg)(\?|#|$)/i
const EMBED_READY_DELAY_MS = 1200

const getMetadataPoster = (metadata?: Record<string, unknown>): string | undefined => {
  const poster = metadata?.poster ?? metadata?.thumbnail
  return typeof poster === 'string' && poster.length > 0 ? poster : undefined
}

const MediaControlCircle: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = ''
}) => (
  <span
    className={`flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-[0px_8px_24px_-4px_rgba(0,0,0,0.35)] ${className}`}
  >
    {children}
  </span>
)

const PlayOverlay: React.FC = () => (
  <div className='absolute inset-0 flex items-center justify-center bg-black/15 group-hover:bg-black/25 transition-colors'>
    <MediaControlCircle className='group-hover:scale-105 transition-transform'>
      <Icon IconComponent={Play} size='md' iconProps={{ weight: 'fill' }} className='text-zinc-600' />
    </MediaControlCircle>
  </div>
)

const LoadingOverlay: React.FC = () => (
  <div className='pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-black/25'>
    <Icon
      IconComponent={Spinner}
      size='lg'
      iconProps={{ weight: 'bold' }}
      className='text-white animate-spin drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]'
    />
  </div>
)

const PosterImage: React.FC<{
  src: string
  youTubeId?: string
  className?: string
}> = ({ src, youTubeId, className = 'w-full h-full object-cover' }) => (
  <img
    src={src}
    alt=''
    className={className}
    onError={e => {
      const img = e.target as HTMLImageElement
      if (youTubeId && !img.src.includes('hqdefault')) {
        img.src = `https://img.youtube.com/vi/${youTubeId}/hqdefault.jpg`
      }
    }}
  />
)

const VideoTile: React.FC<VideoTileProps> = ({ media, fallbackLabel, isPlaying, onPlay }) => {
  const url = media.url ?? ''
  const youTubeId = url.match(YOUTUBE_RE)?.[1]
  const vimeoId = url.match(VIMEO_RE)?.[1]
  const isEmbedVideo = Boolean(youTubeId || vimeoId)
  const isDirectVideo = !isEmbedVideo && (DIRECT_VIDEO_RE.test(url) || url.length > 0)

  const youTubePoster = youTubeId ? `https://img.youtube.com/vi/${youTubeId}/maxresdefault.jpg` : undefined
  const posterUrl = youTubePoster ?? getMetadataPoster(media.metadata)

  const embedUrl = youTubeId
    ? `https://www.youtube.com/embed/${youTubeId}?autoplay=1&rel=0&modestbranding=1`
    : vimeoId
      ? `https://player.vimeo.com/video/${vimeoId}?autoplay=1`
      : url

  const videoRef = useRef<HTMLVideoElement>(null)
  const readyTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const [isPlayerReady, setIsPlayerReady] = useState(false)

  const clearReadyTimer = () => {
    if (readyTimerRef.current) {
      clearTimeout(readyTimerRef.current)
      readyTimerRef.current = undefined
    }
  }

  useEffect(() => {
    return () => clearReadyTimer()
  }, [])

  useEffect(() => {
    clearReadyTimer()
    setIsPlayerReady(false)

    if (!isPlaying) {
      const video = videoRef.current
      if (video) {
        video.pause()
        video.currentTime = 0
        video.controls = false
        video.muted = true
      }
      return
    }

    const video = videoRef.current
    if (!video || !isDirectVideo) return

    video.controls = true
    video.muted = false
    void video.play().catch(() => {})
  }, [isPlaying, isDirectVideo])

  const handleEmbedLoad = () => {
    clearReadyTimer()
    readyTimerRef.current = setTimeout(() => setIsPlayerReady(true), EMBED_READY_DELAY_MS)
  }

  const showLoading = isPlaying && !isPlayerReady
  const showPlayer = isPlaying && isPlayerReady

  return (
    <div className='relative w-full h-full overflow-hidden bg-zinc-900'>
      {posterUrl ? (
        <PosterImage
          src={posterUrl}
          youTubeId={youTubeId}
          className='absolute inset-0 z-0 h-full w-full object-cover'
        />
      ) : isDirectVideo && !isPlaying ? (
        <video
          src={url}
          muted
          playsInline
          preload='metadata'
          className='absolute inset-0 z-0 h-full w-full object-cover'
          aria-hidden
        />
      ) : null}

      {isDirectVideo && (
        <video
          ref={videoRef}
          src={url}
          muted
          playsInline
          preload={isPlaying ? 'auto' : 'metadata'}
          poster={posterUrl}
          className={`absolute inset-0 z-10 h-full w-full object-cover transition-opacity duration-300 ${
            showPlayer ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          aria-label={media.altText ?? fallbackLabel}
          onPlaying={() => setIsPlayerReady(true)}
          onWaiting={() => {
            if (isPlaying) setIsPlayerReady(false)
          }}
        />
      )}

      {isPlaying && isEmbedVideo && (
        <iframe
          src={embedUrl}
          title={media.altText ?? fallbackLabel}
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
          allowFullScreen
          onLoad={handleEmbedLoad}
          className={`absolute inset-0 z-10 h-full w-full border-0 transition-opacity duration-300 ${
            showPlayer ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        />
      )}

      {showLoading && <LoadingOverlay />}

      {!isPlaying && (
        <button
          type='button'
          onClick={onPlay}
          aria-label={`Play ${media.altText ?? fallbackLabel}`}
          className='absolute inset-0 z-30 group'
        >
          <PlayOverlay />
        </button>
      )}
    </div>
  )
}

export default VideoTile
